/**
 * Photo Upload Block - JavaScript
 * 
 * Handles the multi-step modal for uploading images to be attached to cart items.
 * Full-resolution crops and their previews are stored in R2. Private preview URLs are
 * attached as line-item properties and a ZIP of the cropped photos is added to the
 * managed Shopify order note.
 * 
 * @version 2.1.0 — Performance optimized
 * @compatible Horizon Theme (Shopify Online Store 2.0)
 */

// A token normally arrives in 1-3s; allow a slow network, plus extra time when
// the visitor has to interact with the challenge.
const TURNSTILE_TOKEN_TIMEOUT_MS = 25000;
const TURNSTILE_INTERACTIVE_TIMEOUT_MS = 120000;
// Abort an upload only when no bytes move for this long, so slow but healthy
// connections can finish large batches.
const UPLOAD_STALL_TIMEOUT_MS = 30000;
// Time the Worker gets to store a batch after the last byte was sent.
const UPLOAD_RESPONSE_TIMEOUT_MS = 60000;
const UPLOAD_ATTEMPTS = 3;
// Each photo is uploaded as the customer's crop at full source resolution.
// Sources up to MAX_SOURCE_PHOTO_MB are accepted; the cropped JPEG is only
// stepped down when it would exceed MAX_UPLOAD_PHOTO_BYTES (the Worker
// allows 20 MB per file).
const MAX_SOURCE_PHOTO_MB = 50;
const MAX_UPLOAD_PHOTO_BYTES = 18 * 1024 * 1024;
// iOS Safari refuses canvases above ~16.7 megapixels.
const MAX_CANVAS_PIXELS = 16000000;

/**
 * Explain, in customer-facing words, why a file cannot be used, or return
 * null when it is fine. cart-photo-edit.js mirrors these rules.
 * @param {File} file
 * @param {string[]} allowedTypes
 * @param {number} maxSizeMB
 * @returns {string|null}
 */
function describeUnsupportedPhoto(file, allowedTypes, maxSizeMB) {
  const name = String(file?.name || '').toLowerCase();
  if (/\.(heic|heif)$/.test(name) || /image\/hei[cf]/.test(file?.type || '')) {
    return 'HEIC photos are not supported yet. Please save it as JPG or PNG (on iPhone: Settings › Camera › Formats › Most Compatible).';
  }
  if (!allowedTypes.includes(file?.type)) {
    return 'this file type is not supported. Please use a JPG, PNG, WebP or GIF photo.';
  }
  if (!file.size) return 'the file is empty.';
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > maxSizeMB) {
    return `the photo is ${sizeMB.toFixed(1)}MB, larger than the ${maxSizeMB}MB limit. Please choose a smaller copy.`;
  }
  return null;
}

async function decodePhoto(file) {
  if (window.createImageBitmap) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Fall back to <img> decoding below.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Render the customer's crop of a photo at the source's own resolution.
 * `crop` holds the rotation/flips and a rectangle as fractions (0..1) of the
 * rotated image, exactly what the crop modal showed. Quality is kept at 0.95;
 * only a result above maxBytes is lowered in quality and then size.
 * @param {File} file
 * @param {object|((width: number, height: number) => object)} crop
 *   {rotation, flipH, flipV, x, y, w, h}, or a function of the image size returning it
 * @param {number} maxBytes
 * @returns {Promise<File|null>} null when the crop cannot fit maxBytes
 */
async function renderCroppedPhoto(file, crop, maxBytes) {
  const image = await decodePhoto(file);
  const canvas = document.createElement('canvas');
  try {
    if (typeof crop === 'function') crop = crop(image.width, image.height);
    const turned = crop.rotation === 90 || crop.rotation === 270;
    const fullW = turned ? image.height : image.width;
    const fullH = turned ? image.width : image.height;
    const sx = crop.x * fullW;
    const sy = crop.y * fullH;
    const sw = crop.w * fullW;
    const sh = crop.h * fullH;
    const fit = Math.min(1, Math.sqrt(MAX_CANVAS_PIXELS / (sw * sh)));
    const attempts = [[1, 0.95], [1, 0.9], [0.85, 0.9], [0.72, 0.88], [0.6, 0.86]];
    for (const [scale, quality] of attempts) {
      canvas.width = Math.max(1, Math.round(sw * fit * scale));
      canvas.height = Math.max(1, Math.round(sh * fit * scale));
      const context = canvas.getContext('2d');
      // JPEG has no alpha: flatten transparent PNG/WebP onto white.
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.scale(canvas.width / sw, canvas.height / sh);
      context.translate(-sx, -sy);
      context.translate(fullW / 2, fullH / 2);
      context.rotate((crop.rotation * Math.PI) / 180);
      context.scale(crop.flipH ? -1 : 1, crop.flipV ? -1 : 1);
      context.drawImage(image, -image.width / 2, -image.height / 2);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size <= maxBytes) {
        const name = `${String(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`;
        return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
      }
    }
    return null;
  } finally {
    image.close?.();
    canvas.width = 0;
    canvas.height = 0;
  }
}

class PhotoUploadComponent extends HTMLElement {
  constructor() {
    super();

    // State
    this.files = []; // Array of { file: File, dataUrl: string, id: string }
    this.currentStep = 'upload'; // 'upload' | 'preview'
    this.maxFiles = 9;
    this.maxFileSizeMB = MAX_SOURCE_PHOTO_MB;
    this.allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    this.isSubmitting = false;
    this.uploadSessionId = this.generateSessionId();
    this.uploadedBatches = {};
    this.manifestToken = null;
    this.zipUrl = null;
    this.draftDbPromise = null;
    this.draftSaveQueue = Promise.resolve();
    this.draftPersistenceAvailable = true;
    this.activeAbortController = null;
    this._hiddenInputTimer = null; // Debounce timer for updateHiddenInputs
    this.turnstileWidgetId = null;
    this.turnstileReady = null;
    this.turnstileTokenResolver = null;
    this.turnstileContainer = null;
    this.persistedDraftRecords = new Map(); // id -> last record written to IndexedDB

    // Dynamic aspect ratio from variant size (e.g. 50x50mm → 1:1, 80x53mm → 80:53)
    this.arW = 1;
    this.arH = 1;
    this.variantSizeMap = {};

    // DOM references
    this.modal = null;
    this.trigger = null;
    this.fileInput = null;
    this.dropzone = null;
    this.previewGrid = null;
    this.countDisplay = null;

    // Bind methods ONCE in constructor only
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleActionButtonClick = this.handleActionButtonClick.bind(this);
    this.handleRemoveFile = this.handleRemoveFile.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    // Read aspect ratio from data attributes
    this.arW = parseInt(this.dataset.arW) || 1;
    this.arH = parseInt(this.dataset.arH) || 1;
    try { this.variantSizeMap = JSON.parse(this.dataset.variantSizeMap || '{}'); } catch(e) { this.variantSizeMap = {}; }

    this.render();
    this.mountModalToBody();
    this.bindEvents();
    this.initializeTurnstile();

    // Detect initial Package variant and set maxFiles accordingly
    this.detectPackageVariant();

    // Listen for variant changes to update maxFiles dynamically
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    document.addEventListener('variant:update', this.handleVariantUpdate);

    // Also listen for direct variant picker clicks (faster response)
    this.handleVariantClick = this.handleVariantClick.bind(this);
    document.addEventListener('variant:selected', this.handleVariantClick);

    // Listen for variant changes to update aspect ratio
    this._listenVariantRatioChange();

    // Restore an unfinished upload after resolving the current pack limit.
    this.loadExistingFiles().catch((error) => console.warn('[PhotoUpload] Could not restore upload draft:', error));
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.activeAbortController?.abort();
    if (this.modal?.parentNode === document.body) {
      this.modal.remove();
    }
    if (this.turnstileContainer?.parentNode === document.body) {
      this.turnstileContainer.remove();
    }
    document.body.style.overflow = '';
    document.removeEventListener('variant:update', this.handleVariantUpdate);
    document.removeEventListener('variant:selected', this.handleVariantClick);
  }

  /**
   * Main render - creates the trigger button and modal structure
   */
  render() {
    const productTitle = this.dataset.productTitle || 'Product';
    const buttonText = this.dataset.buttonText || 'Upload pictures';
    const requiredCount = parseInt(this.dataset.requiredCount, 10) || 1;

    this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 9;

    this.innerHTML = `
      <button type="button" class="photo-upload-trigger" data-testid="photo-upload-trigger">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <span>${buttonText}</span>
      </button>
      
      <!-- Modal -->
      <div class="photo-upload-modal" data-testid="photo-upload-modal" role="dialog" aria-modal="true" aria-labelledby="photo-upload-title">
        <div class="photo-upload-modal__container">
          <header class="photo-upload-modal__header">
            <h2 class="photo-upload-modal__title" id="photo-upload-title">Upload images</h2>
            <button type="button" class="photo-upload-modal__close" aria-label="Close">&times;</button>
          </header>
          
          <div class="photo-upload-processing" role="status" aria-live="polite" hidden>
            <span class="photo-upload-processing__spinner" aria-hidden="true"></span>
            <span class="photo-upload-processing__text"></span>
          </div>
          <div class="photo-upload-modal__body">
            <!-- Step 1: Upload Source -->
            <div class="photo-upload-step photo-upload-step--upload" data-step="upload">
              <div class="photo-upload-dropzone" data-testid="photo-upload-dropzone">
                <svg class="photo-upload-dropzone__icon" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="8" y="12" width="36" height="36" rx="4"/>
                  <rect x="20" y="16" width="36" height="36" rx="4"/>
                  <circle cx="20" cy="28" r="4"/>
                  <path d="M8 40 l12 -12 l8 8 l12 -12 l4 4"/>
                </svg>
                <p class="photo-upload-dropzone__text">Drag & Drop Images</p>
                <button type="button" class="photo-upload-pc-btn" data-testid="photo-upload-pc-btn">
                  <span class="photo-upload-text-desktop">+ Upload From PC</span>
                  <span class="photo-upload-text-mobile">+ Upload From Gallery</span>
                </button>
              </div>
              
              <div class="photo-upload-sources">
                <p class="photo-upload-sources__label">or choose from</p>
                <div class="photo-upload-sources__icons">
                  <button type="button" class="photo-upload-source-icon" data-action="camera" title="Camera">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </button>
                  <button type="button" class="photo-upload-source-icon" data-action="link" title="Upload bulk links">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              <input 
                type="file" 
                class="photo-upload-input" 
                accept="image/jpeg,image/png,image/webp,image/gif" 
                multiple 
                data-testid="photo-upload-input"
              />
              <!-- Camera specific input -->
              <input 
                type="file" 
                class="photo-upload-camera-input" 
                accept="image/*" 
                capture="environment"
                multiple
                style="display: none;"
              />
            </div>
            
            <!-- Step 2: Preview -->
            <div class="photo-upload-step photo-upload-step--preview" data-step="preview" style="display: none;">
              <div class="photo-upload-preview">
                <div class="photo-upload-preview__grid-wrapper">
                  <div class="photo-upload-preview__grid" data-testid="photo-upload-grid">
                    <!-- Preview items will be inserted here -->
                  </div>
                  
                  <div class="photo-upload-preview__sidebar">
                    <div class="photo-upload-preview__info">
                      <h3>Product Preview</h3>
                      <div class="photo-upload-preview__note">
                        <span class="photo-upload-preview__note-icon"></span>
                        <span>Area beyond the dotted lines will wrap around the edges of your magnets</span>
                      </div>
                      <p class="photo-upload-preview__tip">Click the "Crop" icon near a picture to resize or crop it</p>
                      <p class="photo-upload-preview__count">
                        <strong>Selected</strong> <span data-count>0</span>/<span data-max-count>${this.maxFiles}</span>
                      </p>
                    </div>
                    
                    <div class="photo-upload-sidebar-buttons">
                      <button type="button" class="photo-upload-action-btn" data-testid="photo-upload-action">
                        <span class="photo-upload-action-btn__icon">+</span>
                        <span class="photo-upload-action-btn__text">ADD ${this.maxFiles} MORE</span>
                      </button>
                      <button type="button" class="photo-upload-addtocart-btn" data-testid="photo-upload-addtocart" style="display: none;">
                        ADD TO CART
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Hidden inputs for form submission -->
      <div class="photo-upload-turnstile" aria-hidden="true"></div>
      <div class="photo-upload-hidden-inputs" style="display: none;" data-testid="photo-upload-hidden-inputs">
        <!-- Will contain hidden inputs with image data URLs -->
      </div>
    `;

    // Cache DOM references
    this.trigger = this.querySelector('.photo-upload-trigger');
    this.modal = this.querySelector('.photo-upload-modal');
    this.fileInput = this.querySelector('.photo-upload-input');
    this.dropzone = this.querySelector('.photo-upload-dropzone');
    this.previewGrid = this.querySelector('.photo-upload-preview__grid');
    this.countDisplay = this.querySelector('[data-count]');
    this.uploadStep = this.querySelector('[data-step="upload"]');
    this.previewStep = this.querySelector('[data-step="preview"]');
  }

  /**
   * Bind all event listeners
   * NOTE: Methods are already bound in constructor, do NOT re-bind here
   */
  bindEvents() {
    // Trigger
    this.trigger?.addEventListener('click', this.handleTriggerClick);

    // File Input
    this.fileInput?.addEventListener('change', this.handleFileSelect);

    // PC Upload Button
    this.modal.querySelector('.photo-upload-pc-btn')?.addEventListener('click', () => {
      this.fileInput?.click();
    });

    // Camera Input
    const cameraInput = this.modal.querySelector('.photo-upload-camera-input');
    this.modal.querySelector('[data-action="camera"]')?.addEventListener('click', () => {
      cameraInput?.click();
    });
    cameraInput?.addEventListener('change', (e) => {
      this.handleFileSelect(e);
      e.target.value = ''; // Reset to allow re-selection
    });

    // Bulk Link Upload
    this.modal.querySelector('[data-action="link"]')?.addEventListener('click', this.handleBulkLinkUpload.bind(this));

    // Drag & Drop
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropzone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
    this.dropzone.addEventListener('dragover', this.handleDragOver);
    this.dropzone.addEventListener('dragleave', this.handleDragLeave);
    this.dropzone.addEventListener('drop', this.handleDrop);

    // Modal Close
    this.modal?.querySelector('.photo-upload-modal__close')?.addEventListener('click', this.handleModalClose);
    this.modal?.addEventListener('click', (e) => {
      if (e.target === this.modal) this.handleModalClose();
    });
    document.addEventListener('keydown', this.handleKeyDown);

    // Action Button (Add More)
    this.modal.querySelector('.photo-upload-action-btn')?.addEventListener('click', this.handleActionButtonClick);

    // Add to Cart Button
    this.modal.querySelector('.photo-upload-addtocart-btn')?.addEventListener('click', this.handleAddToCart);

    // Form Intercept
    this.interceptFormSubmit();
  }

  /**
   * Handle Bulk Link Upload
   */
  async handleBulkLinkUpload() {
    const input = prompt("Enter image URLs (one per line):");
    if (!input) return;

    const urls = input.split(/[\n\r,]+/).map(u => u.trim()).filter(u => u);
    if (urls.length === 0) return;

    const validFiles = [];

    // Create a simple loading indicator
    const linkBtn = this.modal.querySelector('[data-action="link"]');
    const originalText = linkBtn.innerHTML;
    linkBtn.innerHTML = '...';

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network error');
        const blob = await response.blob();
        const filename = url.split('/').pop().split('?')[0] || `image-${Date.now()}.jpg`;
        const file = new File([blob], filename, { type: blob.type });

        if (file.type.startsWith('image/')) {
          validFiles.push(file);
        }
      } catch (err) {
        console.warn(`Failed to load image from ${url}:`, err);
      }
    }

    // Restore icon
    linkBtn.innerHTML = originalText;

    if (validFiles.length > 0) {
      this.processFiles(validFiles);
    } else {
      alert("Could not load images. Please ensure URLs are direct image links and accessible (CORS enabled).");
    }
  }

  /**
   * Intercept product form submit to inject photo inputs
   */
  interceptFormSubmit() {
    // Find the product form
    const productFormComponent = document.querySelector('product-form-component');
    const productForm = productFormComponent?.querySelector('form') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!productForm) return;

    // Store reference for cleanup
    this.productForm = productForm;

    // Bind handler
    this.formSubmitHandler = (e) => {
      // Ensure our photo inputs are in the form before submission
      if (this.files && this.files.length > 0) {
        this.syncInputsToProductForm();
      }
    };

    // Use capture phase to run before other handlers
    productForm.addEventListener('submit', this.formSubmitHandler, { capture: true });
  }

  unbindEvents() {
    document.removeEventListener('keydown', this.handleKeyDown);

    // Clean up form submit listener
    if (this.productForm && this.formSubmitHandler) {
      this.productForm.removeEventListener('submit', this.formSubmitHandler, { capture: true });
    }
  }

  getDraftStorageKey() {
    return `photo-upload-draft:${this.dataset.productId || 'product'}`;
  }

  generateSessionId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }

  openDraftDatabase() {
    if (this.draftDbPromise) return this.draftDbPromise;
    this.draftDbPromise = new Promise((resolve, reject) => {
      if (!window.indexedDB) return reject(new Error('IndexedDB is unavailable.'));
      const request = indexedDB.open('dearest-moment-photo-upload', 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('draftFiles')) {
          request.result.createObjectStore('draftFiles', { keyPath: 'key' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('Unable to open photo draft storage.'));
    });
    return this.draftDbPromise;
  }

  draftFileKey(id) {
    return `${this.getDraftStorageKey()}:${id}`;
  }

  waitForTransaction(transaction) {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error || new Error('Photo draft storage failed.'));
      transaction.onabort = () => reject(transaction.error || new Error('Photo draft storage was cancelled.'));
    });
  }

  idbRequest(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('Photo draft read failed.'));
    });
  }

  saveUploadDraft() {
    this.draftSaveQueue = this.draftSaveQueue
      .catch(() => {})
      .then(() => this.persistUploadDraft());
    return this.draftSaveQueue;
  }

  async persistUploadDraft() {
    if (!this.files.length) {
      await this.clearUploadDraft();
      return;
    }
    try {
      const previousRaw = sessionStorage.getItem(this.getDraftStorageKey());
      let previousIds = [];
      try { previousIds = JSON.parse(previousRaw || '{}').files?.map((item) => item.id) || []; } catch { previousIds = []; }
      const database = await this.openDraftDatabase();
      const transaction = database.transaction('draftFiles', 'readwrite');
      const store = transaction.objectStore('draftFiles');
      const activeIds = new Set(this.files.map((item) => item.id));
      for (const id of new Set([...previousIds, ...this.persistedDraftRecords.keys()])) {
        if (!activeIds.has(id)) {
          store.delete(this.draftFileKey(id));
          this.persistedDraftRecords.delete(id);
        }
      }
      // Duplicated photos share one File: store its bytes once and reference
      // it from the copies. Unchanged records are not rewritten, so saving
      // after each upload batch only updates the small metadata.
      const ownerByFile = new Map();
      const written = [];
      for (const item of this.files) {
        const ownerId = ownerByFile.get(item.file);
        if (!ownerId) ownerByFile.set(item.file, item.id);
        const record = ownerId
          ? { key: this.draftFileKey(item.id), fileRef: ownerId, dataUrl: item.dataUrl, crop: item.crop }
          : { key: this.draftFileKey(item.id), file: item.file, dataUrl: item.dataUrl, crop: item.crop };
        const previous = this.persistedDraftRecords.get(item.id);
        if (previous && previous.file === record.file && previous.fileRef === record.fileRef && previous.dataUrl === record.dataUrl && previous.crop === record.crop) continue;
        store.put(record);
        written.push([item.id, record]);
      }
      await this.waitForTransaction(transaction);
      written.forEach(([id, record]) => this.persistedDraftRecords.set(id, record));
      sessionStorage.setItem(this.getDraftStorageKey(), JSON.stringify({
        savedAt: Date.now(),
        productId: this.dataset.productId || '',
        variantId: this.dataset.variantId || '',
        uploadSessionId: this.uploadSessionId,
        uploadedBatches: this.uploadedBatches,
        manifestToken: this.manifestToken,
        zipUrl: this.zipUrl,
        files: this.files.map((item) => ({
          id: item.id,
          name: item.file?.name || 'photo.jpg',
          type: item.file?.type || 'image/jpeg',
        })),
      }));
      this.draftPersistenceAvailable = true;
    } catch (error) {
      this.draftPersistenceAvailable = false;
      console.warn('[PhotoUpload] Could not save upload draft:', error);
    }
  }

  async clearUploadDraft() {
    try {
      const raw = sessionStorage.getItem(this.getDraftStorageKey());
      let ids = this.files.map((item) => item.id);
      try { ids = [...new Set([...ids, ...(JSON.parse(raw || '{}').files?.map((item) => item.id) || [])])]; } catch { /* ignore malformed metadata */ }
      sessionStorage.removeItem(this.getDraftStorageKey());
      ids = [...new Set([...ids, ...this.persistedDraftRecords.keys()])];
      this.persistedDraftRecords.clear();
      if (ids.length && window.indexedDB) {
        const database = await this.openDraftDatabase();
        const transaction = database.transaction('draftFiles', 'readwrite');
        const store = transaction.objectStore('draftFiles');
        ids.forEach((id) => store.delete(this.draftFileKey(id)));
        await this.waitForTransaction(transaction);
      }
    } catch (error) {
      console.warn('[PhotoUpload] Could not clear upload draft:', error);
    }
  }

  /** Restore an unfinished upload for five minutes after a refresh. */
  async loadExistingFiles() {
    try {
      const raw = sessionStorage.getItem(this.getDraftStorageKey());
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft.savedAt || Date.now() - draft.savedAt > 5 * 60 * 1000 || draft.productId !== (this.dataset.productId || '') || !Array.isArray(draft.files)) {
        await this.clearUploadDraft();
        return;
      }
      const database = await this.openDraftDatabase();
      const transaction = database.transaction('draftFiles', 'readonly');
      const store = transaction.objectStore('draftFiles');
      const draftFiles = draft.files.slice(0, this.maxFiles);
      const records = await Promise.all(draftFiles.map((item) => this.idbRequest(store.get(this.draftFileKey(item.id)))));
      const fileById = new Map(draftFiles.map((item, index) => [item.id, records[index]?.file]));
      const restored = draftFiles.map((item, index) => {
        const record = records[index];
        const file = record?.file || fileById.get(record?.fileRef);
        if (!file || !record?.dataUrl) throw new Error(`Original draft file ${item.name || item.id} is missing.`);
        return { id: item.id || this.generateId(), dataUrl: record.dataUrl, crop: record.crop, file };
      });
      // Safari can hand back Blobs from IndexedDB whose data is gone; only
      // restore a draft whose originals can still be read.
      await Promise.all([...new Set(restored.map((item) => item.file))].map(async (file) => {
        const probe = await file.slice(0, 1).arrayBuffer().catch(() => null);
        if (!probe?.byteLength) throw new Error(`Original draft file ${file.name || 'photo'} is no longer readable.`);
      }));
      this.files = restored;
      restored.forEach((item, index) => this.persistedDraftRecords.set(item.id, records[index]));
      this.uploadSessionId = draft.uploadSessionId || this.generateSessionId();
      this.uploadedBatches = draft.uploadedBatches && typeof draft.uploadedBatches === 'object'
        ? draft.uploadedBatches : {};
      this.manifestToken = draft.manifestToken || null;
      this.zipUrl = draft.zipUrl || null;
      if (this.files.length) {
        this.showStep('preview');
        this.renderPreviewGrid();
        this.updateTriggerButton();
        this.updateActionButton();
      }
    } catch (error) {
      await this.clearUploadDraft();
      console.warn('[PhotoUpload] Could not restore upload draft:', error);
    }
  }

  /**
   * Detect the selected pack variant and use its quantity as the photo limit.
   * The option is labelled "Choose your pack" in the storefront, but older
   * products may call it "Package" or "Pack". Supports both radio cards and
   * the theme's dropdown variant picker.
   */
  detectPackageVariant() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    const fieldsets = variantPicker.querySelectorAll('fieldset');
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend, .variant-picker__option-name');
      const legendText = legend?.textContent?.replace(/\s+/g, ' ').trim().toLowerCase() || '';
      const isPackOption = /(?:choose\s+your\s+pack|package|\bpack\b)/i.test(legendText);
      if (!isPackOption) continue;

      // Button/card picker: read the selected label, not the input value only.
      const checkedInput = fieldset.querySelector('input:checked');
      const checkedLabel = checkedInput?.closest('label');
      // Pack cards keep the pack name separate from price/badge text. Read
      // that node first so a price such as "£9" can never become the limit.
      let value = checkedLabel?.querySelector('.variant-pack-card__name')?.textContent?.trim()
        || checkedInput?.value
        || checkedLabel?.textContent?.trim()
        || '';

      // Dropdown picker: the selected option is the source of truth.
      if (!value) {
        const select = fieldset.querySelector('select');
        value = select?.selectedOptions?.[0]?.textContent?.trim() || select?.value || '';
      }

      const num = this.extractNumberFromVariant(value);
      if (num) this.updateMaxFiles(num);
      break;
    }
  }

  /**
   * Handle variant:update event dispatched by VariantPicker after section re-render.
   * Re-detect Package variant and sync variant ID from re-rendered DOM.
   */
  handleVariantUpdate(event) {
    if (this.isSubmitting) return;
    // The picker has already been morphed when this event fires. Read it now
    // so the limit changes in the same task as the variant update.
    this.detectPackageVariant();
    this.syncVariantId();
  }

  /**
   * Handle variant:selected event for immediate feedback before section re-render.
   */
  handleVariantClick(event) {
    if (this.isSubmitting) return;
    // VariantPicker dispatches this after updateSelectedOption(), so the
    // checked input/selected option is already authoritative here.
    this.detectPackageVariant();
    this.syncVariantId();
  }

  /**
 * Sync data-variant-id attribute from the variant picker.
 * Ensures our component always has the correct variant ID after variant changes.
 *
 * Priority order:
 *   1. Variant picker checked input (theme-managed, always correct for current product)
 *   2. URL ?variant= param
 *   3. Keep existing data-variant-id (set by Liquid at page load)
 */
  syncVariantId() {
    // Priority 1: Variant picker checked input — most reliable, always this product
    const variantFromPicker = this.getVariantFromPicker();
    if (variantFromPicker) {
      this.dataset.variantId = variantFromPicker;
      console.log('[PhotoUpload] Synced variant from picker:', variantFromPicker);
      return;
    }

    // Priority 2: URL param
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant) {
      this.dataset.variantId = urlVariant;
      console.log('[PhotoUpload] Synced variant from URL:', urlVariant);
      return;
    }

    // Priority 3: Keep existing data-variant-id (Liquid rendered)
    // No action needed if it's already set from Liquid.
    // If it's not set, it will remain undefined, which is fine.
    console.log('[PhotoUpload] Using existing data-variant-id (fallback):', this.dataset.variantId);
  }

  /**
   * Read variant ID from the variant picker's checked inputs.
   * The variant picker is rendered by the theme for the CURRENT product only.
   * @returns {string|null}
   */
  getVariantFromPicker() {
    const section = this.closest('[data-section-id]') || this.closest('.shopify-section');
    const scope = section || document;

    const variantPicker = scope.querySelector('variant-picker');
    if (!variantPicker) return null;

    // Variant picker inputs have data-variant-id with the correct variant for each option
    const checkedInputs = variantPicker.querySelectorAll('input:checked[data-variant-id]');
    if (checkedInputs.length > 0) {
      // Use the last checked input's variant (most specific — e.g. Package option)
      return checkedInputs[checkedInputs.length - 1].dataset.variantId;
    }

    return null;
  }

  /**
   * Listen for variant changes to update aspect ratio dynamically.
   * Parses size options like "50x50 mm" or "80x53 mm" from variant picker.
   */
  _listenVariantRatioChange() {
    // Instant: fires BEFORE fetch, zero latency
    document.addEventListener('variant:selected', () => {
      const picker = document.querySelector('variant-picker');
      if (!picker) return;
      const selectedElements = Array.from(picker.querySelectorAll('input:checked, select option:checked'));
      for (const el of selectedElements) {
        const val = (el.value || '').toLowerCase().replace(/\s/g, '').replace(/×/g, 'x');
        if (val.includes('x') && val.includes('mm')) {
          const parts = val.split('mm')[0].split('x');
          if (parts.length === 2) {
            const w = parseFloat(parts[0]);
            const h = parseFloat(parts[1]);
            if (w > 0 && h > 0 && (this.arW !== w || this.arH !== h)) {
              this.arW = w;
              this.arH = h;
              this._applyAspectRatio();
              if (this.currentStep === 'preview' && this.files.length > 0) {
                this.renderPreviewGrid();
              }
            }
          }
        }
      }
    });

    // Confirmed: fires AFTER section re-render fetch
    document.addEventListener('variant:update', (e) => {
      const vid = String(e.detail?.resource?.id || '');
      if (vid) this._applyVariantRatio(vid);
    });
  }

  /**
   * Apply aspect ratio from variant size map lookup.
   */
  _applyVariantRatio(vid) {
    const sizeInfo = this.variantSizeMap[vid];
    if (sizeInfo && sizeInfo.w > 0 && sizeInfo.h > 0) {
      if (this.arW === sizeInfo.w && this.arH === sizeInfo.h) return;
      this.arW = sizeInfo.w;
      this.arH = sizeInfo.h;
      this._applyAspectRatio();
      if (this.currentStep === 'preview' && this.files.length > 0) {
        this.renderPreviewGrid();
      }
    }
  }

  /**
   * Apply current aspect ratio to CSS custom properties.
   */
  _applyAspectRatio() {
    const block = this.closest('.photo-upload-block');
    if (block) {
      block.style.setProperty('--photo-upload-ar-w', String(this.arW));
      block.style.setProperty('--photo-upload-ar-h', String(this.arH));
    }
    console.log(`[PhotoUpload] Aspect ratio updated: ${this.arW}:${this.arH}`);
  }

  /**
   * Extract number from variant value string.
   * e.g., "6 items" -> 6, "9 items" -> 9, "12" -> 12
   */
  extractNumberFromVariant(value) {
    if (!value) return null;
    const match = value.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Update maxFiles and re-render UI accordingly.
   * If user already uploaded more photos than new max, trim excess with warning.
   */
  updateMaxFiles(newMax) {
    if (newMax === this.maxFiles) return;
    if (newMax < 1 || newMax > 50) return; // sanity check

    const oldMax = this.maxFiles;
    this.maxFiles = newMax;

    // Trim excess files if needed
    if (this.files.length > newMax) {
      const removed = this.files.length - newMax;
      const confirmed = window.confirm(`This pack allows ${newMax} photos. Remove the last ${removed} photo${removed === 1 ? '' : 's'}?`);
      if (!confirmed) {
        this.maxFiles = oldMax;
        return;
      }
      this.files = this.files.slice(0, newMax);
      this.saveUploadDraft();
      console.info(`Package changed: trimmed ${removed} excess photo(s) to fit ${newMax} max.`);
    }

    // Update grid columns dynamically
    this.updateGridLayout();

    // Update sidebar count display
    const maxCountEl = this.modal?.querySelector('[data-max-count]');
    if (maxCountEl) {
      maxCountEl.textContent = newMax;
    }

    // Re-render if preview is showing
    if (this.currentStep === 'preview' && this.files.length > 0) {
      this.renderPreviewGrid();
    }
    this.updateActionButton();
    this.debouncedUpdateHiddenInputs();
    this.updateTriggerButton();
  }

  /**
   * Update grid layout: always 3 columns, gap 10px.
   * Grid height is controlled by CSS flex layout, no JS maxHeight needed.
   */
  updateGridLayout() {
    if (!this.previewGrid) return;
    this.previewGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
    this.previewGrid.style.gap = '10px';
  }

  /**
   * Scroll the preview grid to show the last uploaded item
   */
  scrollToLastItem() {
    if (!this.previewGrid) return;
    requestAnimationFrame(() => {
      const items = this.previewGrid.querySelectorAll('.photo-upload-preview__item');
      if (items.length > 0) {
        const lastItem = items[items.length - 1];
        lastItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }

  /**
   * Open modal
   */
  handleTriggerClick() {
    this.modal?.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Show appropriate step based on current files
    if (this.files.length > 0) {
      this.showStep('preview');
    } else {
      this.showStep('upload');
    }

    // Focus trap
    this.modal?.querySelector('.photo-upload-modal__close')?.focus();
  }

  /**
   * Close modal
   */
  handleModalClose() {
    this.modal?.classList.remove('active');
    document.body.style.overflow = '';
  }

  /**
   * Handle ESC key
   */
  handleKeyDown(e) {
    if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
      this.handleModalClose();
    }
  }

  /**
   * Switch between upload and preview steps
   */
  showStep(step) {
    this.currentStep = step;

    if (step === 'upload') {
      this.uploadStep.style.display = '';
      this.previewStep.style.display = 'none';
    } else {
      this.uploadStep.style.display = 'none';
      this.previewStep.style.display = '';
      this.renderPreviewGrid();
    }
  }

  /**
   * Handle drag over
   */
  handleDragOver(e) {
    e.preventDefault();
    this.dropzone?.classList.add('drag-over');
  }

  /**
   * Handle drag leave
   */
  handleDragLeave(e) {
    e.preventDefault();
    this.dropzone?.classList.remove('drag-over');
  }

  /**
   * Handle file drop
   */
  handleDrop(e) {
    e.preventDefault();
    this.dropzone?.classList.remove('drag-over');

    const files = Array.from(e.dataTransfer?.files || []);
    this.processFiles(files);
  }

  /**
   * Handle file input change
   */
  handleFileSelect(e) {
    const files = Array.from(e.target?.files || []);
    this.processFiles(files);

    // Reset input to allow re-selecting same file
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  /** Show (text) or hide (null) the "optimising photos" overlay in the modal. */
  setProcessingStatus(text) {
    const overlay = this.modal?.querySelector('.photo-upload-processing');
    if (!overlay) return;
    overlay.hidden = !text;
    const label = overlay.querySelector('.photo-upload-processing__text');
    if (label) label.textContent = text || '';
  }

  /**
   * Process and validate selected files
   */
  async processFiles(files) {
    let validFiles = [];
    const rejected = [];
    let overLimit = 0;
    const startingCount = this.files.length;

    for (const file of files) {
      const problem = describeUnsupportedPhoto(file, this.allowedTypes, this.maxFileSizeMB);
      if (problem) {
        console.warn(`Skipping ${file.name}: ${problem}`);
        rejected.push(`• ${file.name || 'Photo'}: ${problem}`);
        continue;
      }

      // Check max files limit
      if (this.files.length + validFiles.length >= this.maxFiles) {
        overLimit++;
        continue;
      }

      validFiles.push(file);
    }

    // Tell the customer about every photo that was not added, instead of
    // silently ignoring it.
    const notices = [];
    if (rejected.length) {
      notices.push(`${rejected.length === 1 ? 'This photo' : 'These photos'} could not be added:\n${rejected.join('\n')}`);
    }
    if (overLimit) {
      const plural = (count) => `${count} photo${count === 1 ? '' : 's'}`;
      notices.push(startingCount >= this.maxFiles
        ? `Your pack is already full (${this.maxFiles} of ${this.maxFiles} photos). Remove a photo or choose a bigger pack to add more.`
        : `You selected ${plural(validFiles.length + overLimit)}, but your pack holds ${this.maxFiles}. The first ${plural(validFiles.length)} ${validFiles.length === 1 ? 'was' : 'were'} added and ${overLimit} left out. Remove a photo or choose a bigger pack to add more.`);
    }
    if (notices.length) alert(notices.join('\n\n'));

    // Convert to data URLs in parallel for speed
    const prepared = await Promise.allSettled(
      validFiles.map(file => this.fileToDataUrl(file).then(({ dataUrl, crop }) => ({
        file,
        dataUrl,
        crop,
        id: this.generateId()
      })))
    );
    const results = prepared.filter((item) => item.status === 'fulfilled').map((item) => item.value);
    const failedFiles = prepared.filter((item) => item.status === 'rejected').length;
    if (failedFiles > 0) {
      alert(`${failedFiles} image${failedFiles === 1 ? '' : 's'} could not be read. Please choose another file.`);
    }
    this.files.push(...results);

    // Switch to preview if files added
    if (this.files.length > 0) {
      this.showStep('preview');
      await this.saveUploadDraft();
      // Without draft storage (e.g. Safari private browsing) uploads still
      // work; only refresh recovery is lost, so this is not worth a popup.
      if (!this.draftPersistenceAvailable) console.info('[PhotoUpload] Refresh recovery is unavailable in this browser.');
      this.debouncedUpdateHiddenInputs();
      this.updateTriggerButton();
      this.updateActionButton();
      this.scrollToLastItem();
    }
  }

  /**
   * Convert File to base64 data URL with auto-crop to current aspect ratio.
   * 
   * Auto-crops from center to arW:arH ratio, then resizes to max 800px at 85% JPEG.
   * This ensures thumbnails in the modal always show the cropped version.
   */
  fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          resolve({
            dataUrl: this.autoCropRatio(img, 800, 0.85),
            crop: this.centerCrop(img.width, img.height),
          });
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * The centered arW:arH crop as fractions of the image, matching what
   * autoCropRatio draws. Stored per photo so the full-resolution crop that is
   * uploaded is the one the customer saw.
   */
  centerCrop(width, height) {
    const targetRatio = (this.arW || 1) / (this.arH || 1);
    const w = width / height > targetRatio ? (height * targetRatio) / width : 1;
    const h = width / height > targetRatio ? 1 : width / targetRatio / height;
    return { rotation: 0, flipH: false, flipV: false, x: (1 - w) / 2, y: (1 - h) / 2, w, h };
  }

  /**
   * Auto-crop an image to arW:arH ratio (center crop) and resize.
   * Falls back to 1:1 if arW/arH are not set.
   * @param {HTMLImageElement} img - The source image
   * @param {number} maxSize - Maximum width/height in pixels
   * @param {number} quality - JPEG quality (0.0 to 1.0)
   * @returns {string} Base64 data URL of the cropped image
   */
  autoCropRatio(img, maxSize, quality) {
    const w = img.width;
    const h = img.height;
    const targetRatio = (this.arW || 1) / (this.arH || 1);
    const imgRatio = w / h;

    let cropW, cropH, sx, sy;
    if (imgRatio > targetRatio) {
      // Image is wider than target — crop width
      cropH = h;
      cropW = Math.round(h * targetRatio);
      sx = Math.round((w - cropW) / 2);
      sy = 0;
    } else {
      // Image is taller than target — crop height
      cropW = w;
      cropH = Math.round(w / targetRatio);
      sx = 0;
      sy = Math.round((h - cropH) / 2);
    }

    // Output size: fit within maxSize
    let outW, outH;
    if (cropW >= cropH) {
      outW = Math.min(cropW, maxSize);
      outH = Math.round(outW / targetRatio);
    } else {
      outH = Math.min(cropH, maxSize);
      outW = Math.round(outH * targetRatio);
    }

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, outW, outH);
    return canvas.toDataURL('image/jpeg', quality);
  }

  /**
   * Compress an image to a target max dimension and JPEG quality.
   * Used both for initial upload and after cropping.
   * @param {HTMLImageElement} img - The source image
   * @param {number} maxSize - Maximum width/height in pixels
   * @param {number} quality - JPEG quality (0.0 to 1.0)
   * @returns {string} Base64 data URL
   */
  compressImage(img, maxSize, quality) {
    let width = img.width;
    let height = img.height;

    if (width > maxSize || height > maxSize) {
      const ratio = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', quality);
  }

  /**
   * Re-compress a data URL for Shopify cart properties.
   * 400×400 @ 60% ≈ 8-12KB per image for sharp cart thumbnails.
   * @param {string} dataUrl - The source data URL to re-compress
   * @returns {Promise<string>} Base64 data URL (~8-12KB)
   */
  compressForCart(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(this.compressImage(img, 400, 0.6));
      };
      img.onerror = () => {
        resolve(dataUrl);
      };
      img.src = dataUrl;
    });
  }

  /**
   * Create a thumbnail for cart display and order detail in Shopify Admin.
   * 400×400 @ 60% ≈ 8-12KB per image. Sharp enough for cart preview grid.
   * @param {string} dataUrl - The source data URL
   * @returns {Promise<string>} Base64 data URL
   */
  compressForOrderThumbnail(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(this.compressImage(img, 400, 0.6));
      };
      img.onerror = () => {
        resolve(dataUrl);
      };
      img.src = dataUrl;
    });
  }

  // ========================================
  // CLOUDFLARE WORKER INTEGRATION
  // ========================================

  /**
   * Get the code-owned Cloudflare Worker endpoint.
   * No secret is ever present in the storefront.
   */
  getWorkerConfig() {
    const url = this.dataset.workerUrl?.trim();
    const siteKey = this.dataset.turnstileSiteKey?.trim();
    if (!url) return null;
    // An empty site key means Turnstile is switched off.
    return { url: url.replace(/\/$/, ''), siteKey: siteKey || '' };
  }

  /**
   * Move the fixed modal outside product/section stacking contexts.
   * Some theme sections use transform/contain/isolation, which can make a
   * fixed descendant render underneath later page content despite z-index.
   */
  mountModalToBody() {
    if (this.modal && this.modal.parentNode !== document.body) {
      document.body.appendChild(this.modal);
    }
  }

  initializeTurnstile() {
    const config = this.getWorkerConfig();
    // The Turnstile mount is intentionally outside the portalled modal so it
    // remains owned by the component and survives modal reparenting. It is
    // moved to <body> before the widget renders (never afterwards) so that an
    // interactive challenge can be shown above the modal instead of hanging
    // invisibly underneath it.
    const container = this.querySelector('.photo-upload-turnstile');
    if (!config?.siteKey || !container) return;
    document.body.appendChild(container);
    this.turnstileContainer = container;
    const setInteractive = (interactive) => {
      container.classList.toggle('is-interactive', interactive);
      container.setAttribute('aria-hidden', String(!interactive));
    };

    this.turnstileReady = new Promise((resolve, reject) => {
      let pollId;
      let settled = false;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        clearInterval(pollId);
        window.removeEventListener('load', check);
        callback(value);
      };
      const render = () => {
        try {
          const fail = (message) => {
            setInteractive(false);
            this.turnstileTokenResolver?.reject(new Error(message));
          };
          this.turnstileWidgetId = window.turnstile.render(container, {
            sitekey: config.siteKey,
            action: 'photo_upload',
            // Only run when execute() is called, stay hidden unless the visitor
            // must interact, and surface failures to us instead of retrying
            // silently (each upload attempt requests its own fresh token).
            execution: 'execute',
            appearance: 'interaction-only',
            retry: 'never',
            'refresh-expired': 'manual',
            callback: (token) => {
              setInteractive(false);
              this.turnstileTokenResolver?.resolve(token);
            },
            'error-callback': () => {
              fail('Upload verification failed. Please try again.');
              return true;
            },
            'expired-callback': () => fail('Upload verification expired. Please try again.'),
            'timeout-callback': () => fail('The security check timed out. Please try again.'),
            'unsupported-callback': () => fail('This browser cannot complete the security check. Please update it or try another browser.'),
            'before-interactive-callback': () => {
              setInteractive(true);
              this.turnstileTokenResolver?.extend(TURNSTILE_INTERACTIVE_TIMEOUT_MS);
            },
            'after-interactive-callback': () => setInteractive(false),
          });
          finish(resolve);
        } catch (error) {
          finish(reject, error);
        }
      };
      const check = () => {
        if (window.turnstile?.render) render();
      };

      // The Turnstile script is deferred and may finish after this component
      // connects. Wait for the actual API instead of relying on window.load,
      // which may already have fired before this listener is registered.
      check();
      if (!settled) {
        pollId = window.setInterval(check, 50);
        window.addEventListener('load', check);
        window.setTimeout(() => {
          if (!settled) finish(reject, new Error('Turnstile script did not load.'));
        }, 15000);
      }
    });
  }

  async getTurnstileToken() {
    if (!this.getWorkerConfig()?.siteKey) return null;
    if (!this.turnstileReady) {
      throw new Error('Upload verification is unavailable. Please refresh and try again.');
    }
    await this.turnstileReady;
    if (this.turnstileWidgetId === null || !window.turnstile) {
      throw new Error('Upload verification is unavailable. Please refresh and try again.');
    }
    // Never wait forever: content blockers or a challenge that cannot be
    // displayed would otherwise leave the button spinning indefinitely.
    return new Promise((resolve, reject) => {
      let timer = null;
      const settle = (callback, value) => {
        clearTimeout(timer);
        if (this.turnstileTokenResolver === resolver) this.turnstileTokenResolver = null;
        callback(value);
      };
      const resolver = {
        resolve: (token) => settle(resolve, token),
        reject: (error) => settle(reject, error),
        extend: (timeoutMs) => {
          clearTimeout(timer);
          // A silent widget is almost always blocked, so retrying only delays
          // the message; mark it final.
          timer = setTimeout(() => resolver.reject(Object.assign(new Error(
            'The security check could not be completed. Please check your connection, pause any ad or content blockers for this site, and try again.'
          ), { final: true })), timeoutMs);
        },
      };
      this.turnstileTokenResolver?.reject(new Error('Upload verification was restarted.'));
      this.turnstileTokenResolver = resolver;
      resolver.extend(TURNSTILE_TOKEN_TIMEOUT_MS);
      try {
        window.turnstile.reset(this.turnstileWidgetId);
        window.turnstile.execute(this.turnstileWidgetId);
      } catch (error) {
        resolver.reject(error);
      }
    });
  }

  /**
   * Convert a base64 data URL to a Blob (for uploading cropped images).
   * @param {string} dataUrl - Base64 data URL
   * @returns {Blob}
   */
  dataUrlToBlob(dataUrl) {
    const [header, base64Data] = dataUrl.split(',');
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const binary = atob(base64Data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  }

  /**
   * Upload each photo's full-resolution crop and its preview to Cloudflare R2.
   * Batches are bounded by both file count and total request bytes.
   * @param {HTMLElement} statusBtn - Button element to show progress
   * A unique Turnstile token protects each batch; failed uploads never
   * fall back to base64, so Shopify receives only durable Worker URLs.
   * @param {{ url: string, siteKey: string }} config - Worker configuration
   * @returns {Promise<{files: Array<{index: number, url: string}>, manifestToken: string, zipUrl: string}>}
   */
  async uploadAllToWorker(statusBtn, config) {
    const MAX_BATCH_FILES = 20;
    // Smaller requests keep each Worker invocation light and make a retry
    // after a dropped connection cheap; progress is reported per byte anyway.
    const MAX_BATCH_BYTES = 20 * 1024 * 1024;
    const results = new Array(this.files.length);
    const startedAt = performance.now();

    this.setUploadStatus(statusBtn, 'PREPARING PHOTOS...');
    // Every photo is stored as the customer's crop at full source resolution;
    // each one is rendered separately so duplicates with different crops differ.
    const croppedPhotos = [];
    for (let index = 0; index < this.files.length; index++) {
      croppedPhotos.push(await this.renderCroppedPhotoFile(this.files[index], index));
    }
    const originalRefs = this.files.map(() => null);
    const previews = this.files.map((item) => this.dataUrlToBlob(item.dataUrl));
    const itemBytes = this.files.map((item, index) => previews[index].size + croppedPhotos[index].size);
    const totalBytes = itemBytes.reduce((sum, bytes) => sum + bytes, 0) || 1;
    let sentBytes = 0;
    this.updateUploadProgress(statusBtn, 0);

    for (let batchStart = 0; batchStart < this.files.length;) {
      let batchEnd = batchStart;
      let batchBytes = 0;
      while (batchEnd < this.files.length && batchEnd - batchStart < MAX_BATCH_FILES) {
        const nextBytes = itemBytes[batchEnd];
        if (batchEnd > batchStart && batchBytes + nextBytes > MAX_BATCH_BYTES) break;
        batchBytes += nextBytes;
        batchEnd++;
      }
      const batchRefs = originalRefs.slice(batchStart, batchEnd);
      const batchKey = this.getBatchKey(batchStart, batchEnd);
      const cachedBatch = this.uploadedBatches[batchKey];
      if (cachedBatch?.files?.length === batchEnd - batchStart) {
        sentBytes += batchBytes;
        this.updateUploadProgress(statusBtn, sentBytes / totalBytes);
        results.splice(batchStart, batchEnd - batchStart, ...cachedBatch.files);
        this.manifestToken = cachedBatch.manifestToken;
        this.zipUrl = cachedBatch.zipUrl;
        batchStart = batchEnd;
        continue;
      }

      const buildForm = (turnstileToken) => {
        const formData = new FormData();
        for (let i = batchStart; i < batchEnd; i++) {
          formData.append('original', croppedPhotos[i], croppedPhotos[i].name);
          formData.append('preview', previews[i], `preview-${i + 1}.jpg`);
        }
        formData.append('originalRefs', JSON.stringify(batchRefs));
        formData.append('turnstileToken', turnstileToken);
        formData.append('uploadSessionId', this.uploadSessionId);
        formData.append('idempotencyKey', batchKey);
        formData.append('startIndex', String(batchStart));
        formData.append('totalCount', String(this.files.length));
        return formData;
      };

      const workerStartedAt = performance.now();
      const payload = await this.uploadBatchWithRetry(`${config.url}/upload`, buildForm, (fraction) => {
        this.updateUploadProgress(statusBtn, (sentBytes + batchBytes * fraction) / totalBytes);
      });
      console.info(`[PhotoUpload] Batch ${batchStart + 1}-${batchEnd} (${(batchBytes / 1048576).toFixed(1)}MB) stored in ${Math.round(performance.now() - workerStartedAt)}ms`);
      if (!Array.isArray(payload.files) || payload.files.length !== batchEnd - batchStart) {
        throw new Error(payload.error || 'Image upload failed. Please try again.');
      }
      if (!payload.manifestToken || !payload.zipUrl) throw new Error('Image upload did not create a downloadable archive.');
      const batchResults = [];
      payload.files.forEach((file, offset) => {
        if (!file?.url) throw new Error('Image upload returned an invalid link.');
        batchResults.push({ index: batchStart + offset, url: file.url });
        results[batchStart + offset] = batchResults[offset];
      });
      this.manifestToken = payload.manifestToken;
      this.zipUrl = payload.zipUrl;
      this.uploadedBatches[batchKey] = { files: batchResults, manifestToken: payload.manifestToken, zipUrl: payload.zipUrl };
      await this.saveUploadDraft();
      sentBytes += batchBytes;
      this.updateUploadProgress(statusBtn, sentBytes / totalBytes);
      batchStart = batchEnd;
    }

    console.info(`[PhotoUpload] Prepared and uploaded ${this.files.length} photo(s), ${(totalBytes / 1048576).toFixed(1)}MB, in ${Math.round(performance.now() - startedAt)}ms`);

    return { files: results, manifestToken: this.manifestToken, zipUrl: this.zipUrl };
  }

  /**
   * Render one photo's saved crop at full resolution as an in-memory File.
   * The source is decoded here, so a photo that can no longer be read from
   * the device is named to the customer instead of sending a truncated body.
   * @param {{file: File, crop?: object}} item
   * @param {number} index - Zero-based photo position, for the error message
   * @returns {Promise<File>}
   */
  async renderCroppedPhotoFile(item, index) {
    const label = `Photo ${index + 1} (${item.file?.name || 'photo'})`;
    let cropped = null;
    try {
      cropped = await renderCroppedPhoto(
        item.file,
        item.crop || ((width, height) => this.centerCrop(width, height)),
        MAX_UPLOAD_PHOTO_BYTES
      );
    } catch (error) {
      console.warn(`[PhotoUpload] Could not render ${label}:`, error);
    }
    if (!cropped?.size) {
      throw Object.assign(new Error(
        `${label} can no longer be read from your device. Please remove it, add it again, then press Add to Cart.`
      ), { final: true });
    }
    return cropped;
  }

  getBatchKey(start, end) {
    const fingerprint = this.files.slice(start, end)
      .map((item, offset) => `${item.id}:${item.file?.name || ''}:${item.file?.size || 0}:${item.file?.lastModified || 0}:${item.dataUrl?.length || 0}:${item.dataUrl?.slice(-24) || ''}:${JSON.stringify(item.crop || null)}`)
      .join('|');
    return `${this.uploadSessionId}:${start}-${end}:${this.hashString(fingerprint)}`;
  }

  hashString(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index++) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16);
  }

  setUploadStatus(statusBtn, text) {
    if (!statusBtn) return;
    const statusText = statusBtn.querySelector('.photo-upload-action-btn__text');
    if (statusText) statusText.textContent = text;
    else statusBtn.textContent = text;
  }

  /** @param {number} fraction - Share of all upload bytes sent, 0..1 */
  updateUploadProgress(statusBtn, fraction) {
    if (!statusBtn) return;
    const percent = Math.max(0, Math.min(100, Math.floor(fraction * 100)));
    statusBtn.style.setProperty('--photo-upload-progress', `${percent}%`);
    this.setUploadStatus(statusBtn, `UPLOADING ${percent}%`);
  }

  /**
   * Upload one batch, retrying dropped connections, stalls and temporary
   * server errors. Every attempt carries a fresh single-use Turnstile token;
   * the batch idempotency key makes a retried, already-stored batch replay.
   * @param {string} url
   * @param {(turnstileToken: string) => FormData} buildForm
   * @param {(fraction: number) => void} onProgress
   * @returns {Promise<object>} Parsed Worker response
   */
  async uploadBatchWithRetry(url, buildForm, onProgress) {
    let lastError;
    for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt++) {
      try {
        const turnstileStartedAt = performance.now();
        const turnstileToken = await this.getTurnstileToken();
        console.info(`[PhotoUpload] Turnstile ready in ${Math.round(performance.now() - turnstileStartedAt)}ms`);
        onProgress(0);
        const response = await this.sendUploadRequest(url, buildForm(turnstileToken), onProgress, turnstileToken);
        if (response.ok) return response.payload;
        // A form the Worker could not parse means the body arrived incomplete
        // (a dropped or interrupted connection): worth retrying.
        const incomplete = response.payload.code === 'invalid_form';
        lastError = new Error(incomplete
          ? 'Your photos did not arrive completely.'
          : response.payload.error || `Image upload failed (${response.status}). Please try again.`);
        // 403 is a rejected (possibly stale) token; 409 and other 4xx are final.
        if (!incomplete && response.status !== 403 && response.status < 500) throw Object.assign(lastError, { final: true });
      } catch (error) {
        if (error.final || error.name === 'AbortError') throw error;
        lastError = error;
      }
      if (attempt < UPLOAD_ATTEMPTS) {
        console.warn(`[PhotoUpload] Upload attempt ${attempt} failed, retrying:`, lastError);
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }
    }
    throw new Error(`${lastError?.message || 'Image upload failed.'} Your photos are saved on this page, so you can press Add to Cart again.`);
  }

  /**
   * POST a form with XMLHttpRequest so upload progress is observable. The
   * request is aborted only when no bytes move for UPLOAD_STALL_TIMEOUT_MS,
   * or when the Worker does not answer within UPLOAD_RESPONSE_TIMEOUT_MS of
   * the last byte, so slow but healthy connections are never cut off. The
   * Turnstile token also travels as a header so the Worker can verify it
   * before the (possibly slow) body arrives, before the token expires.
   */
  sendUploadRequest(url, formData, onProgress, turnstileToken) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const controller = new AbortController();
      this.activeAbortController = controller;
      let timer = null;
      let timedOut = false;
      const arm = (timeoutMs) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          timedOut = true;
          xhr.abort();
        }, timeoutMs);
      };
      const cleanup = () => {
        clearTimeout(timer);
        if (this.activeAbortController === controller) this.activeAbortController = null;
      };
      controller.signal.addEventListener('abort', () => xhr.abort());

      xhr.open('POST', url);
      if (turnstileToken) xhr.setRequestHeader('X-Turnstile-Token', turnstileToken);
      xhr.upload.addEventListener('progress', (event) => {
        arm(UPLOAD_STALL_TIMEOUT_MS);
        if (event.lengthComputable && event.total > 0) onProgress(Math.min(1, event.loaded / event.total));
      });
      xhr.upload.addEventListener('load', () => {
        onProgress(1);
        arm(UPLOAD_RESPONSE_TIMEOUT_MS);
      });
      xhr.addEventListener('load', () => {
        cleanup();
        let payload = {};
        try { payload = JSON.parse(xhr.responseText || '{}'); } catch { payload = {}; }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, payload });
      });
      xhr.addEventListener('error', () => {
        cleanup();
        reject(new Error('The connection was interrupted while uploading.'));
      });
      xhr.addEventListener('abort', () => {
        cleanup();
        if (timedOut) reject(new Error('The upload stalled because the connection is too slow or unstable.'));
        else reject(Object.assign(new Error('Upload cancelled.'), { name: 'AbortError' }));
      });
      arm(UPLOAD_STALL_TIMEOUT_MS);
      xhr.send(formData);
    });
  }

  /**
   * Generate a unique ID
   */
  generateId() {
    return 'photo_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Update action button state based on file count
   * Shows "ADD X MORE" when < maxFiles, hides when full
   * The separate "ADD TO CART" button shows whenever files.length > 0
   */
  updateActionButton() {
    const actionBtn = this.modal?.querySelector('.photo-upload-action-btn');
    const addToCartBtn = this.modal?.querySelector('.photo-upload-addtocart-btn');
    if (!actionBtn) return;

    const iconSpan = actionBtn.querySelector('.photo-upload-action-btn__icon');
    const textSpan = actionBtn.querySelector('.photo-upload-action-btn__text');

    const remaining = this.maxFiles - this.files.length;

    if (remaining > 0) {
      // Still can add more photos - show add more button
      if (iconSpan) {
        iconSpan.textContent = '+';
        iconSpan.style.display = '';
      }
      if (textSpan) textSpan.textContent = `ADD ${remaining} MORE`;
      actionBtn.style.display = '';
    } else {
      // Reuse the primary button as the checkout action when the pack is full.
      if (iconSpan) {
        iconSpan.textContent = '';
        iconSpan.style.display = 'none';
      }
      if (textSpan) textSpan.textContent = 'ADD TO CART';
      actionBtn.style.display = '';
    }

    // Keep the legacy secondary button hidden; the primary button changes
    // into ADD TO CART so customers get one unambiguous next action.
    if (addToCartBtn) {
      addToCartBtn.style.display = 'none';
    }
  }

  /**
   * Handle action button click - always triggers file upload (add more)
   */
  handleActionButtonClick() {
    if (this.files.length >= this.maxFiles) {
      this.handleAddToCart();
      return;
    }
    this.fileInput?.click();
  }

  /**
   * Render preview grid
   */
  renderPreviewGrid() {
    if (!this.previewGrid) return;

    let html = '';

    // Render existing files
    this.files.forEach((item, index) => {
      html += `
        <div class="photo-upload-preview__item" data-id="${item.id}" style="aspect-ratio: ${this.arW}/${this.arH}">
          <img src="${item.dataUrl}" alt="Photo ${index + 1}" />
          <div class="photo-upload-preview__actions">
            <button type="button" class="photo-upload-preview__action-btn photo-upload-preview__action-btn--delete" data-action="delete" data-id="${item.id}" title="Delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
            <button type="button" class="photo-upload-preview__action-btn" data-action="crop" data-id="${item.id}" title="Crop">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/>
                <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/>
              </svg>
            </button>
            <button type="button" class="photo-upload-preview__action-btn" data-action="duplicate" data-id="${item.id}" title="Duplicate">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    });

    // Render one empty slot for every remaining photo in the selected pack.
    // Example: 7 selected out of 12 => 5 clickable "Add more" cells.
    const remainingSlots = Math.max(0, this.maxFiles - this.files.length);
    for (let slot = 0; slot < remainingSlots; slot++) {
      html += `
        <div class="photo-upload-preview__add-more" data-testid="photo-upload-add-more" style="aspect-ratio: ${this.arW}/${this.arH}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Add more</span>
        </div>
      `;
    }

    this.previewGrid.innerHTML = html;

    // Apply grid layout based on current maxFiles
    this.updateGridLayout();

    // Update count display: "Selected X / Y"
    if (this.countDisplay) {
      this.countDisplay.textContent = this.files.length;
    }
    const maxCountEl = this.modal?.querySelector('[data-max-count]');
    if (maxCountEl) {
      maxCountEl.textContent = this.maxFiles;
    }

    // Bind delete buttons
    this.previewGrid.querySelectorAll('[data-action="delete"]').forEach(btn => {
      btn.addEventListener('click', this.handleRemoveFile);
    });

    // Bind crop buttons
    this.previewGrid.querySelectorAll('[data-action="crop"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.openCropModal(e.currentTarget.dataset.id));
    });

    // Bind duplicate buttons
    this.previewGrid.querySelectorAll('[data-action="duplicate"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleDuplicateFile(e.currentTarget.dataset.id));
    });

    // Bind add more cell
    this.previewGrid.querySelectorAll('.photo-upload-preview__add-more').forEach((addMore) => {
      addMore.addEventListener('click', () => {
        this.fileInput?.click();
      });
    });
  }

  /**
   * Remove a file
   */
  handleRemoveFile(e) {
    const id = e.currentTarget.dataset.id;
    this.files = this.files.filter(f => f.id !== id);
    this.saveUploadDraft();

    this.renderPreviewGrid();
    this.debouncedUpdateHiddenInputs();
    this.updateTriggerButton();
    this.updateActionButton();

    // Go back to upload step if no files
    if (this.files.length === 0) {
      this.showStep('upload');
    }
  }

  /**
   * Duplicate a file — clone and insert right after the original
   * @param {string} fileId - ID of the file to duplicate
   */
  handleDuplicateFile(fileId) {
    if (this.files.length >= this.maxFiles) {
      alert(`Maximum ${this.maxFiles} photos allowed. Remove a photo first.`);
      return;
    }

    const sourceIndex = this.files.findIndex(f => f.id === fileId);
    if (sourceIndex === -1) return;

    const source = this.files[sourceIndex];
    const clone = {
      file: source.file,
      dataUrl: source.dataUrl,
      crop: source.crop ? { ...source.crop } : undefined,
      id: this.generateId()
    };

    // Insert clone right after the source
    this.files.splice(sourceIndex + 1, 0, clone);
    this.saveUploadDraft();

    this.renderPreviewGrid();
    this.debouncedUpdateHiddenInputs();
    this.updateTriggerButton();
    this.updateActionButton();
    this.scrollToLastItem();
  }

  /**
   * Open crop modal for a specific image
   * @param {string} fileId - ID of the file to crop
   */
  openCropModal(fileId) {
    const file = this.files.find(f => f.id === fileId);
    if (!file) return;

    // Create crop modal
    const cropModal = document.createElement('div');
    cropModal.className = 'photo-upload-crop-modal';
    cropModal.innerHTML = `
      <div class="photo-upload-crop-modal__overlay"></div>
      <div class="photo-upload-crop-modal__container">
        <div class="photo-upload-crop-modal__header">
          <h3>Crop Image</h3>
          <button type="button" class="photo-upload-crop-modal__close">&times;</button>
        </div>
        <div class="photo-upload-crop-modal__body">
          <div class="photo-upload-crop-modal__canvas-wrapper">
            <canvas class="photo-upload-crop-modal__canvas"></canvas>
            <div class="photo-upload-crop-modal__crop-area">
              <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--nw" data-handle="nw"></div>
              <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--ne" data-handle="ne"></div>
              <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--sw" data-handle="sw"></div>
              <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--se" data-handle="se"></div>
            </div>
          </div>
          <div class="photo-upload-crop-modal__toolbar">
            <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="flip-h" title="Flip Horizontal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M16 3l4 4-4 4"/>
                <path d="M20 7H4"/>
                <path d="M8 21l-4-4 4-4"/>
                <path d="M4 17h16"/>
              </svg>
              <span>Flip H</span>
            </button>
            <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="flip-v" title="Flip Vertical">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M3 8l4-4 4 4"/>
                <path d="M7 4v16"/>
                <path d="M21 16l-4 4-4-4"/>
                <path d="M17 20V4"/>
              </svg>
              <span>Flip V</span>
            </button>
            <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="rotate-cw" title="Rotate 90°">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M23 4v6h-6"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              <span>Rotate</span>
            </button>
          </div>
        </div>
        <div class="photo-upload-crop-modal__footer">
          <button type="button" class="photo-upload-crop-modal__cancel">Cancel</button>
          <button type="button" class="photo-upload-crop-modal__apply">Apply Crop</button>
        </div>
      </div>
    `;

    document.body.appendChild(cropModal);

    // Transform state
    let flipH = false;
    let flipV = false;
    let rotation = 0; // 0, 90, 180, 270

    // Load image from original file for best quality
    const img = new Image();
    let objectUrl = null;
    if (file.file) {
      objectUrl = URL.createObjectURL(file.file);
    }

    img.onload = () => {
      const canvas = cropModal.querySelector('.photo-upload-crop-modal__canvas');
      const ctx = canvas.getContext('2d');
      const wrapper = cropModal.querySelector('.photo-upload-crop-modal__canvas-wrapper');
      const cropArea = cropModal.querySelector('.photo-upload-crop-modal__crop-area');

      // Fit the canvas inside the space left by header, toolbar and footer.
      // Both constraints matter: a portrait image must fit height, while a
      // landscape image must fit width. This keeps the editor scroll-free.
      const isMobile = window.innerWidth <= 767;
      const maxCanvasWidth = Math.min(isMobile ? window.innerWidth - 32 : 600, window.innerWidth - 48);
      const maxCanvasHeight = Math.max(120, window.innerHeight - (isMobile ? 220 : 250));

      /**
       * Get effective display dimensions based on rotation
       */
      const getDisplayDims = () => {
        const isRotated = rotation === 90 || rotation === 270;
        const srcW = isRotated ? img.height : img.width;
        const srcH = isRotated ? img.width : img.height;

        let dw = srcW;
        let dh = srcH;
        if (dw > maxCanvasWidth || dh > maxCanvasHeight) {
          const ratio = Math.min(maxCanvasWidth / dw, maxCanvasHeight / dh);
          dw = Math.round(srcW * ratio);
          dh = Math.round(srcH * ratio);
        }
        return { displayWidth: dw, displayHeight: dh };
      };

      /**
       * Redraw the canvas with current transforms
       */
      const redrawCanvas = () => {
        const { displayWidth, displayHeight } = getDisplayDims();

        canvas.width = displayWidth;
        canvas.height = displayHeight;
        wrapper.style.width = displayWidth + 'px';
        wrapper.style.height = displayHeight + 'px';

        ctx.save();
        ctx.clearRect(0, 0, displayWidth, displayHeight);

        // Move to center for transforms
        ctx.translate(displayWidth / 2, displayHeight / 2);

        // Apply rotation
        ctx.rotate((rotation * Math.PI) / 180);

        // Apply flips
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

        // Draw image centered (use original dims since rotation is handled by ctx)
        const isRotated = rotation === 90 || rotation === 270;
        const drawW = isRotated ? displayHeight : displayWidth;
        const drawH = isRotated ? displayWidth : displayHeight;
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

        ctx.restore();

        // Reset crop area to fit new dimensions with arW:arH ratio
        const cropRatio = (self.arW || 1) / (self.arH || 1);
        if (displayWidth / displayHeight > cropRatio) {
          cropH = displayHeight;
          cropW = Math.round(cropH * cropRatio);
        } else {
          cropW = displayWidth;
          cropH = Math.round(cropW / cropRatio);
        }
        cropX = Math.round((displayWidth - cropW) / 2);
        cropY = Math.round((displayHeight - cropH) / 2);
        updateCropAreaUI();
      };

      // Initial draw
      let { displayWidth, displayHeight } = getDisplayDims();
      canvas.width = displayWidth;
      canvas.height = displayHeight;
      wrapper.style.width = displayWidth + 'px';
      wrapper.style.height = displayHeight + 'px';
      ctx.drawImage(img, 0, 0, displayWidth, displayHeight);

      // Setup crop area (arW:arH ratio, centered)
      const self = this;
      const minCropDim = 50;
      const cropRatio = (this.arW || 1) / (this.arH || 1);
      let cropW, cropH;
      if (displayWidth / displayHeight > cropRatio) {
        cropH = displayHeight;
        cropW = Math.round(cropH * cropRatio);
      } else {
        cropW = displayWidth;
        cropH = Math.round(cropW / cropRatio);
      }
      let cropX = Math.round((displayWidth - cropW) / 2);
      let cropY = Math.round((displayHeight - cropH) / 2);

      const updateCropAreaUI = () => {
        cropArea.style.width = cropW + 'px';
        cropArea.style.height = cropH + 'px';
        cropArea.style.left = cropX + 'px';
        cropArea.style.top = cropY + 'px';
      };

      updateCropAreaUI();

      // --- Toolbar events ---
      cropModal.querySelector('[data-tool="flip-h"]').addEventListener('click', () => {
        flipH = !flipH;
        redrawCanvas();
      });
      cropModal.querySelector('[data-tool="flip-v"]').addEventListener('click', () => {
        flipV = !flipV;
        redrawCanvas();
      });
      cropModal.querySelector('[data-tool="rotate-cw"]').addEventListener('click', () => {
        rotation = (rotation + 90) % 360;
        redrawCanvas();
        // Update local display dims reference
        const dims = getDisplayDims();
        displayWidth = dims.displayWidth;
        displayHeight = dims.displayHeight;
      });

      // Drag and resize state
      let isDragging = false;
      let isResizing = false;
      let resizeHandle = null;
      let startX = 0, startY = 0, startCropX = 0, startCropY = 0, startCropW = 0, startCropH = 0;

      // Handle drag start on crop area (not on resize handles)
      const handleCropAreaDown = (e) => {
        // Normalize touch/mouse
        const point = e.touches ? e.touches[0] : e;
        if (e.target.dataset.handle) return; // Skip if clicking on resize handle

        isDragging = true;
        startX = point.clientX;
        startY = point.clientY;
        startCropX = cropX;
        startCropY = cropY;
        e.preventDefault();
      };

      cropArea.addEventListener('mousedown', handleCropAreaDown);
      cropArea.addEventListener('touchstart', handleCropAreaDown, { passive: false });

      // Handle resize start on handles
      cropModal.querySelectorAll('.photo-upload-crop-modal__resize-handle').forEach(handle => {
        const handleResizeDown = (e) => {
          const point = e.touches ? e.touches[0] : e;
          isResizing = true;
          resizeHandle = (e.target || e.currentTarget).dataset.handle;
          startX = point.clientX;
          startY = point.clientY;
          startCropX = cropX;
          startCropY = cropY;
          startCropW = cropW;
          startCropH = cropH;
          e.preventDefault();
          e.stopPropagation();
        };
        handle.addEventListener('mousedown', handleResizeDown);
        handle.addEventListener('touchstart', handleResizeDown, { passive: false });
      });

      // Handle mouse/touch move for both drag and resize
      const handlePointerMove = (e) => {
        const point = e.touches ? e.touches[0] : e;
        // Use current display dimensions
        const curDims = getDisplayDims();
        const dw = curDims.displayWidth;
        const dh = curDims.displayHeight;

        if (isDragging) {
          const dx = point.clientX - startX;
          const dy = point.clientY - startY;

          cropX = Math.max(0, Math.min(dw - cropW, startCropX + dx));
          cropY = Math.max(0, Math.min(dh - cropH, startCropY + dy));

          updateCropAreaUI();
        } else if (isResizing) {
          const dx = point.clientX - startX;
          const dy = point.clientY - startY;
          const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
          let newW, newH, newX, newY;

          switch (resizeHandle) {
            case 'se':
              newW = startCropW + delta;
              newH = newW / cropRatio;
              newX = startCropX;
              newY = startCropY;
              break;
            case 'sw':
              newW = startCropW - delta;
              newH = newW / cropRatio;
              newX = startCropX + startCropW - newW;
              newY = startCropY;
              break;
            case 'ne':
              newW = startCropW + delta;
              newH = newW / cropRatio;
              newX = startCropX;
              newY = startCropY + startCropH - newH;
              break;
            case 'nw':
              newW = startCropW - delta;
              newH = newW / cropRatio;
              newX = startCropX + startCropW - newW;
              newY = startCropY + startCropH - newH;
              break;
            default:
              return;
          }

          // Validate bounds
          if (newW >= minCropDim && newH >= minCropDim &&
            newX >= 0 && newY >= 0 &&
            newX + newW <= dw && newY + newH <= dh) {
            cropW = Math.round(newW);
            cropH = Math.round(newH);
            cropX = Math.round(newX);
            cropY = Math.round(newY);
            updateCropAreaUI();
          }
        }
      };

      const handlePointerUp = () => {
        isDragging = false;
        isResizing = false;
        resizeHandle = null;
      };

      document.addEventListener('mousemove', handlePointerMove);
      document.addEventListener('mouseup', handlePointerUp);
      document.addEventListener('touchmove', handlePointerMove, { passive: false });
      document.addEventListener('touchend', handlePointerUp);

      // Apply crop + transforms
      cropModal.querySelector('.photo-upload-crop-modal__apply').addEventListener('click', () => {
        const curDims = getDisplayDims();
        const dw = curDims.displayWidth;
        const dh = curDims.displayHeight;

        const isRotated = rotation === 90 || rotation === 270;
        const fullW = isRotated ? img.height : img.width;
        const fullH = isRotated ? img.width : img.height;

        const transformCanvas = document.createElement('canvas');
        transformCanvas.width = fullW;
        transformCanvas.height = fullH;
        const tCtx = transformCanvas.getContext('2d');

        tCtx.translate(fullW / 2, fullH / 2);
        tCtx.rotate((rotation * Math.PI) / 180);
        tCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

        const drawW = isRotated ? fullH : fullW;
        const drawH = isRotated ? fullW : fullH;
        tCtx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

        // Crop from the transformed canvas (ratio-aware)
        const scaleX = fullW / dw;
        const scaleY = fullH / dh;

        const actualX = cropX * scaleX;
        const actualY = cropY * scaleY;
        const actualW = cropW * scaleX;
        const actualH = cropH * scaleY;

        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = Math.round(actualW);
        cropCanvas.height = Math.round(actualH);
        const cropCtx = cropCanvas.getContext('2d');

        cropCtx.drawImage(
          transformCanvas,
          actualX, actualY, actualW, actualH,
          0, 0, Math.round(actualW), Math.round(actualH)
        );

        const tempImg = new Image();
        tempImg.onload = () => {
          file.dataUrl = this.compressImage(tempImg, 800, 0.85);
          file.crop = {
            rotation, flipH, flipV,
            x: cropX / dw, y: cropY / dh, w: cropW / dw, h: cropH / dh,
          };
          cleanup();
          this.renderPreviewGrid();
          this.debouncedUpdateHiddenInputs();
        };
        tempImg.src = cropCanvas.toDataURL('image/jpeg', 0.9);
      });

      // Cleanup on close
      const cleanup = () => {
        document.removeEventListener('mousemove', handlePointerMove);
        document.removeEventListener('mouseup', handlePointerUp);
        document.removeEventListener('touchmove', handlePointerMove);
        document.removeEventListener('touchend', handlePointerUp);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        cropModal.remove();
      };

      cropModal.querySelector('.photo-upload-crop-modal__close').addEventListener('click', cleanup);
      cropModal.querySelector('.photo-upload-crop-modal__cancel').addEventListener('click', cleanup);
      cropModal.querySelector('.photo-upload-crop-modal__overlay').addEventListener('click', cleanup);
    };

    img.src = objectUrl || file.dataUrl;
  }

  /**
   * Debounced version of updateHiddenInputs.
   * Coalesces rapid calls (add/remove/duplicate) into a single DOM update.
   */
  debouncedUpdateHiddenInputs() {
    if (this._hiddenInputTimer) clearTimeout(this._hiddenInputTimer);
    this._hiddenInputTimer = setTimeout(() => this.updateHiddenInputs(), 150);
  }

  /**
   * Update hidden inputs for form submission.
   * Lightweight: only stores metadata (count + id). Photo data is compressed
   * on-demand at Add to Cart time to avoid redundant CPU work.
   */
  updateHiddenInputs() {
    this.saveUploadDraft();
    const container = this.querySelector('.photo-upload-hidden-inputs');
    if (!container) return;

    let html = '';

    // Only store count + upload_id in hidden inputs.
    // Actual photo data is built at submit time in handleAddToCart.
    html += `<input type="hidden" name="properties[_photo_count]" value="${this.files.length}" />`;
    html += `<input type="hidden" name="properties[_upload_id]" value="${Date.now().toString()}" />`;

    container.innerHTML = html;

    // Sync to product form
    this.syncInputsToProductForm();
  }

  /**
   * Sync hidden inputs to the product form for AJAX submission
   */
  syncInputsToProductForm() {
    // Find the product form
    const productFormComponent = this.closest('product-form-component') ||
      document.querySelector('product-form-component');
    const productForm = productFormComponent?.querySelector('form') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!productForm) return;

    // Remove existing photo inputs from form
    productForm.querySelectorAll('input[data-photo-upload="true"]').forEach(el => el.remove());

    // Clone and add new inputs to form
    const hiddenInputs = this.querySelectorAll('.photo-upload-hidden-inputs input');
    hiddenInputs.forEach(input => {
      const clone = input.cloneNode(true);
      clone.style.display = 'none';
      clone.setAttribute('data-photo-upload', 'true');
      productForm.appendChild(clone);
    });
  }

  /**
   * Update trigger button appearance
   */
  updateTriggerButton() {
    if (!this.trigger) return;

    if (this.files.length > 0) {
      this.trigger.classList.add('photo-upload-trigger--filled');
      this.trigger.querySelector('span').textContent = `${this.files.length} photo${this.files.length > 1 ? 's' : ''} selected`;
    } else {
      this.trigger.classList.remove('photo-upload-trigger--filled');
      this.trigger.querySelector('span').textContent = this.dataset.buttonText || 'Upload pictures';
    }
  }

  async getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Could not verify the cart (${response.status}).`);
    return response.json();
  }

  findSessionInCart(cart) {
    return cart?.items?.find((item) => item.properties?._upload_session_id === this.uploadSessionId) || null;
  }

  /** Add once even when Shopify accepted a request whose response was lost. */
  async addCartItemSafely(variantId, properties) {
    let cart = await this.getCart();
    const existing = this.findSessionInCart(cart);
    if (existing) return { item: existing, cart, reconciled: true };

    let lastError = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId), quantity: 1, properties }),
          signal: controller.signal,
        });
        const text = await response.text();
        let payload = null;
        try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }
        if (response.ok && !payload?.status) return { item: payload, reconciled: false };
        const message = payload?.description || payload?.message || text || `Cart error: ${response.status}`;
        if (![429, 500, 502, 503, 504].includes(response.status)) throw new Error(message);
        lastError = new Error(message);
      } catch (error) {
        lastError = error?.name === 'AbortError' ? new Error('Cart confirmation timed out.') : error;
      } finally {
        clearTimeout(timer);
      }

      cart = await this.getCart();
      const reconciled = this.findSessionInCart(cart);
      if (reconciled) return { item: reconciled, cart, reconciled: true };
      if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
    }
    throw lastError || new Error('Failed to add the product to cart.');
  }

  async syncPhotoOrderNote() {
    if (!window.DearestPhotoOrderNote) {
      throw new Error('The order-note service is unavailable. Please refresh and try again.');
    }
    return window.DearestPhotoOrderNote.sync();
  }

  /**
   * Upload originals, add the product exactly once, and synchronize its ZIP URL
   * into the managed cart note before reporting success.
   */
  async handleAddToCart() {
    if (this.files.length === 0) {
      alert('Please select at least one photo');
      return;
    }

    if (this.isSubmitting) return;
    this.isSubmitting = true;

    // Update the visible primary button immediately. The legacy secondary
    // button remains hidden, but is kept disabled for defensive compatibility.
    const submitBtn = this.modal?.querySelector('.photo-upload-action-btn');
    const addToCartBtn = this.modal?.querySelector('.photo-upload-addtocart-btn');
    if (submitBtn) {
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
      submitBtn.querySelector('.photo-upload-action-btn__icon')?.style.setProperty('display', 'none');
      const submitText = submitBtn.querySelector('.photo-upload-action-btn__text');
      if (submitText) submitText.textContent = 'PREPARING PHOTOS...';
    }
    if (addToCartBtn) addToCartBtn.disabled = true;

    try {
      // Get variant ID — use multiple sources in priority order:
      // 0. Variant picker (most reliable — theme-managed, always this product)
      // 1. URL param (updated by VariantPicker via history.replaceState)
      // 2. data-variant-id attribute (set by Liquid at page load)
      let variantId = null;

      // Source 0: Variant picker checked input — always correct for this product
      const pickerVariant = this.getVariantFromPicker();
      if (pickerVariant) {
        variantId = pickerVariant;
        console.log('[PhotoUpload] Variant from picker:', variantId);
      }

      // Source 1: URL query param ?variant=xxx
      if (!variantId) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariant = urlParams.get('variant');
        if (urlVariant) {
          variantId = urlVariant;
          console.log('[PhotoUpload] Variant from URL:', variantId);
        }
      }

      // Source 2: data-variant-id attribute (Liquid rendered at page load)
      if (!variantId) {
        variantId = this.dataset.variantId;
        console.log('[PhotoUpload] Variant from data attribute (fallback):', variantId);
      }

      if (!variantId) {
        throw new Error('Could not determine product variant');
      }

      // Log variant source for debugging
      console.log(`[PhotoUpload] Submitting variant ${variantId} for product ${this.dataset.productId}`);

      // Each property is a durable Worker URL, carried to Shopify Admin with the order.
      const properties = {
        '_photo_count': this.files.length.toString(),
        '_upload_id': Date.now().toString()
      };

      const workerConfig = this.getWorkerConfig();
      if (!workerConfig) throw new Error('Image upload service is unavailable. Please refresh and try again.');
      const uploadBundle = await this.uploadAllToWorker(submitBtn, workerConfig);
      properties._upload_session_id = this.uploadSessionId;
      properties._photo_manifest_token = uploadBundle.manifestToken;
      for (const result of uploadBundle.files) properties[`_Photo ${result.index + 1}`] = result.url;

      if (submitBtn) {
        const submitText = submitBtn.querySelector('.photo-upload-action-btn__text');
        if (submitText) submitText.textContent = 'ADDING TO CART...';
        else submitBtn.textContent = 'ADDING TO CART...';
      }

      // Debug: log total properties size
      const totalSize = Object.values(properties).reduce((sum, v) => sum + v.length, 0);
      console.log(`[PhotoUpload] Cart properties total size: ${(totalSize / 1024).toFixed(1)}KB for ${this.files.length} photos`);

      // Reconcile each ambiguous retry against the private upload session ID.
      const cartStartedAt = performance.now();
      const cartResult = await this.addCartItemSafely(variantId, properties);
      console.info(`[PhotoUpload] Cart responded in ${Math.round(performance.now() - cartStartedAt)}ms`);
      if (submitBtn) {
        const submitText = submitBtn.querySelector('.photo-upload-action-btn__text');
        if (submitText) submitText.textContent = 'PREPARING DOWNLOAD LINK...';
      }
      await this.syncPhotoOrderNote();

      // Close modal
      this.handleModalClose();

      // Remember file count before clearing
      const addedCount = this.files.length;

      // Clear files after successful add
      this.files = [];
      await this.clearUploadDraft();
      // The next photo set is a new cart line with its own manifest; reusing
      // the session would overwrite this set's ZIP and be mistaken for it in
      // the cart.
      this.uploadSessionId = this.generateSessionId();
      this.uploadedBatches = {};
      this.manifestToken = null;
      this.zipUrl = null;
      this.updateHiddenInputs();
      this.updateTriggerButton();

      // Update cart count in the header
      this.updateCartBubble();



      // Dispatch cart update event for theme integration
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartResult.cart || cartResult.item }));

      // Show success popup with the number of items just added
      this.showSuccessPopup(addedCount);

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error.message || 'Failed to add to cart. Please try again.');
    } finally {
      this.isSubmitting = false;
      if (submitBtn) {
        submitBtn.classList.remove('is-loading');
        submitBtn.style.removeProperty('--photo-upload-progress');
        submitBtn.disabled = false;
      }
      if (addToCartBtn) addToCartBtn.disabled = false;
      this.updateActionButton();
    }
  }

  /**
   * Update cart bubble/count in the header
   */
  async updateCartBubble() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Try common selectors for cart count
      const cartBubbles = document.querySelectorAll(
        '.cart-bubble-count, [data-cart-count], .cart-count, .cart-item-count, cart-bubble-component'
      );

      cartBubbles.forEach(el => {
        if (el.tagName.toLowerCase() === 'cart-bubble-component') {
          // Dispatch event for custom component
          el.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        } else {
          el.textContent = cart.item_count;
        }
      });

      // Also dispatch a global event for the theme
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      // Try refreshing cart sections via Section Render API
      const sectionsToRefresh = document.querySelectorAll('[data-section-id]');
      if (sectionsToRefresh.length > 0) {
        // Use Shopify section rendering API to refresh cart-related sections
        const sectionIds = [];
        sectionsToRefresh.forEach(el => {
          const id = el.dataset.sectionId;
          if (id && el.closest('.cart-items-component, cart-bubble-component, [data-cart-section]')) {
            sectionIds.push(id);
          }
        });
      }
    } catch (e) {
      console.warn('Could not update cart bubble:', e);
    }
  }

  /**
   * Show success popup after add to cart
   * @param {number} addedCount - Number of items just added
   */
  async showSuccessPopup(addedCount = 1) {
    // Remove any existing success popups
    document.querySelectorAll('.photo-upload-success').forEach(el => el.remove());

    // Fetch current cart item count
    let cartItemCount = addedCount;
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      cartItemCount = cart.item_count || addedCount;
    } catch (e) {
      console.warn('[PhotoUpload] Could not fetch cart count for popup:', e);
    }

    const popup = document.createElement('div');
    popup.className = 'photo-upload-success';
    popup.innerHTML = `
      <button type="button" class="photo-upload-success__close">&times;</button>
      <p class="photo-upload-success__message">
        Product has been successfully added to the shopping cart
      </p>
      <div class="photo-upload-success__buttons">
        <a href="/cart" class="photo-upload-success__btn photo-upload-success__btn--secondary">
          View your cart ( ${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'} added)
        </a>
        <a href="/checkout" class="photo-upload-success__btn photo-upload-success__btn--primary">
          SECURE CHECKOUT
        </a>
      </div>
      <a href="#" class="photo-upload-success__more-link" onclick="this.closest('.photo-upload-success').remove(); return false;">
        GET MORE MAGNETS
      </a>
      <div class="photo-upload-success__consent">
        <label class="photo-upload-toggle">
          <input type="checkbox" />
          <span class="photo-upload-toggle__slider"></span>
        </label>
        <span>I agree to have my order featured in your social media content.</span>
      </div>
    `;

    document.body.appendChild(popup);

    // Close button
    popup.querySelector('.photo-upload-success__close')?.addEventListener('click', () => {
      popup.remove();
    });

    // Auto-remove after 10 seconds
    setTimeout(() => {
      popup.remove();
    }, 10000);
  }


  /**
   * Get files for external access
   */
  getFiles() {
    return this.files;
  }

  /**
   * Clear all files
   */
  clearFiles() {
    this.files = [];
    this.clearUploadDraft();
    this.updateHiddenInputs();
    this.updateTriggerButton();
    this.updateActionButton();
    this.showStep('upload');
  }
}

// Register custom element
if (!customElements.get('photo-upload-component')) {
  customElements.define('photo-upload-component', PhotoUploadComponent);
}
